import type { ApiKeyApiScopeInput } from '../../types';

export const API_KEY_ACTIONS = ['canExecute', 'canCreate', 'canDelete'] as const;
export type ApiKeyUseCaseId =
  | 'selectedScopes'
  | 'chatWithOneAgent'
  | 'thirdPartyRuntime'
  | 'thirdPartyProvisioning'
  | 'cliLogin';

export const API_KEY_SCOPE_PRESETS = {
  chatWithOneAgent: (agentPublicId: string): ApiKeyApiScopeInput[] => [
    { scope: 'agents:execute', resourceType: 'agent', resourcePublicId: agentPublicId },
    { scope: 'chat_sessions:create', resourceType: 'agent', resourcePublicId: agentPublicId },
    { scope: 'chat_messages:create', resourceType: 'agent', resourcePublicId: agentPublicId },
    { scope: 'chat_messages:read', resourceType: 'agent', resourcePublicId: agentPublicId },
    { scope: 'files:generate', resourceType: 'agent', resourcePublicId: agentPublicId },
    { scope: 'files:read', resourceType: 'agent', resourcePublicId: agentPublicId },
  ],
  thirdPartyRuntime: (clientPublicId: string): ApiKeyApiScopeInput[] => [
    {
      scope: 'agents:read',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'agents:execute',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'chat_sessions:create',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'chat_messages:create',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'chat_messages:read',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'files:generate',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'files:read',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'usage:read',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
  ],
  thirdPartyProvisioning: (clientPublicId: string): ApiKeyApiScopeInput[] => [
    {
      scope: 'integration_clients:read',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'integration_clients:update',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'agents:create',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    { scope: 'agents:read', resourceType: 'integration_client', resourcePublicId: clientPublicId },
    {
      scope: 'agents:update',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'knowledge:create',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'knowledge:read',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'knowledge:update',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    {
      scope: 'knowledge:sync',
      resourceType: 'integration_client',
      resourcePublicId: clientPublicId,
    },
    { scope: 'files:upload', resourceType: 'integration_client', resourcePublicId: clientPublicId },
    { scope: 'files:read', resourceType: 'integration_client', resourcePublicId: clientPublicId },
  ],
};

export const API_KEY_USE_CASES: Array<{
  id: ApiKeyUseCaseId;
  title: string;
  descriptionKey: string;
  description: string;
  resourceLabelKey: string;
  resourceLabel: string;
  resourceRequired: boolean;
}> = [
  {
    id: 'selectedScopes',
    title: 'Selected scopes',
    descriptionKey: 'apiKeys.modal.useCaseDescriptions.selectedScopes',
    description: 'Personal API key for selected service or resource actions.',
    resourceLabelKey: 'apiKeys.modal.resource',
    resourceLabel: 'Resource',
    resourceRequired: false,
  },
  {
    id: 'chatWithOneAgent',
    title: 'Chat with one agent',
    descriptionKey: 'apiKeys.modal.useCaseDescriptions.chatWithOneAgent',
    description: 'Backend clients can create sessions and messages for one selected agent only.',
    resourceLabelKey: 'apiKeys.modal.agentResource',
    resourceLabel: 'Agent',
    resourceRequired: true,
  },
  {
    id: 'thirdPartyRuntime',
    title: 'Third-party runtime',
    descriptionKey: 'apiKeys.modal.useCaseDescriptions.thirdPartyRuntime',
    description:
      'A platform backend such as NailMap can chat through agents bound to one integration client.',
    resourceLabelKey: 'apiKeys.modal.integrationClientResource',
    resourceLabel: 'Integration client',
    resourceRequired: true,
  },
  {
    id: 'thirdPartyProvisioning',
    title: 'Third-party provisioning',
    descriptionKey: 'apiKeys.modal.useCaseDescriptions.thirdPartyProvisioning',
    description:
      'A trusted platform backend can create tenant agents and bindings under one integration client.',
    resourceLabelKey: 'apiKeys.modal.integrationClientResource',
    resourceLabel: 'Integration client',
    resourceRequired: true,
  },
  {
    id: 'cliLogin',
    title: 'CLI Login (Headless / SSH)',
    descriptionKey: 'apiKeys.modal.useCaseDescriptions.cliLogin',
    description:
      'Dedicated key for AppAI CLI login in headless or SSH environments. The CLI will verify your package and exchange this key for temporary CLI session tokens.',
    resourceLabelKey: 'apiKeys.modal.resource',
    resourceLabel: 'Resource',
    resourceRequired: false,
  },
];

export const API_SCOPE_DESCRIPTIONS: Record<string, string> = {
  'agents:create': 'Create tenant agents during provisioning.',
  'agents:read': 'Read allowed agent metadata.',
  'agents:update': 'Update tenant agents during provisioning.',
  'agents:execute': 'Run the selected or bound agent.',
  'chat_sessions:create': 'Create chat sessions.',
  'chat_messages:create': 'Send chat messages.',
  'chat_messages:read': 'Read chat messages and responses.',
  'knowledge:create': 'Create knowledge resources for provisioned agents.',
  'knowledge:read': 'Read knowledge resources for provisioned agents.',
  'knowledge:update': 'Update knowledge resources for provisioned agents.',
  'knowledge:sync': 'Trigger knowledge sync jobs.',
  'files:upload': 'Upload files for provisioned knowledge.',
  'files:generate': 'Generate file artifacts through enabled agent tools.',
  'files:read': 'Download files created or scoped to this key.',
  'integration_clients:read': 'Read the selected integration client.',
  'integration_clients:update': 'Create or update tenant-agent bindings.',
  'usage:read': 'Read usage attributed to this integration client.',
};

export const API_SCOPE_DESCRIPTION_KEYS: Record<string, string> = {
  'agents:create': 'apiKeys.modal.scopeDescriptions.agentsCreate',
  'agents:read': 'apiKeys.modal.scopeDescriptions.agentsRead',
  'agents:update': 'apiKeys.modal.scopeDescriptions.agentsUpdate',
  'agents:execute': 'apiKeys.modal.scopeDescriptions.agentsExecute',
  'chat_sessions:create': 'apiKeys.modal.scopeDescriptions.chatSessionsCreate',
  'chat_messages:create': 'apiKeys.modal.scopeDescriptions.chatMessagesCreate',
  'chat_messages:read': 'apiKeys.modal.scopeDescriptions.chatMessagesRead',
  'knowledge:create': 'apiKeys.modal.scopeDescriptions.knowledgeCreate',
  'knowledge:read': 'apiKeys.modal.scopeDescriptions.knowledgeRead',
  'knowledge:update': 'apiKeys.modal.scopeDescriptions.knowledgeUpdate',
  'knowledge:sync': 'apiKeys.modal.scopeDescriptions.knowledgeSync',
  'files:upload': 'apiKeys.modal.scopeDescriptions.filesUpload',
  'files:generate': 'apiKeys.modal.scopeDescriptions.filesGenerate',
  'files:read': 'apiKeys.modal.scopeDescriptions.filesRead',
  'integration_clients:read': 'apiKeys.modal.scopeDescriptions.integrationClientsRead',
  'integration_clients:update': 'apiKeys.modal.scopeDescriptions.integrationClientsUpdate',
  'usage:read': 'apiKeys.modal.scopeDescriptions.usageRead',
};

export const INTEGRATION_CLIENT_ENVIRONMENTS = ['live', 'test'] as const;
